import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Container, Spinner, Card, Row, Col } from 'react-bootstrap';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { TasksProvider } from './contexts/TasksContext';
import { ChatProvider } from './contexts/ChatContext';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import TelegramAuth from './components/TelegramAuth';
import Dashboard from './components/Dashboard';
import ProfileTab from './components/ProfileTab';
import UsersTab from './components/UsersTab';
import NotificationsTab from './components/NotificationsTab';
import NotificationToasts from './components/NotificationToasts';
import TasksTab from './components/TasksTab';
import SettingsTab from './components/SettingsTab';
import ChatPage from './pages/ChatPage';
import { ChatToast } from './components/chat/ChatToast';
import InactiveAccountPage from './pages/InactiveAccountPage';

const APP_VERSION = __APP_VERSION__;

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading...</p>
        </div>
      </Container>
    );
  }
  return user ? children : <Navigate to="/_admin/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading...</p>
        </div>
      </Container>
    );
  }
  return user ? <Navigate to="/_admin/profile" replace /> : children;
};

const AuthPage = () => {
  const { user, loading, logout, loginWithTelegram, apiKeyValid = true } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showTelegramAuth, setShowTelegramAuth] = useState(false);
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  if (loading) {
    return (
      <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Проверка авторизации...</p>
        </div>
      </Container>
    );
  }

  useEffect(() => {
    const checkTelegramDataOnReturn = () => {
      const telegramData = localStorage.getItem('telegram_auth_data');
      if (telegramData && !user) {
        console.log('🔄 Processing Telegram data on page return');
        try {
          const parsedData = JSON.parse(telegramData);
          handleTelegramAuth(parsedData);
          localStorage.removeItem('telegram_auth_data');
        } catch (err) {
          console.error('Error processing Telegram data on return:', err);
        }
      }
    };

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data && event.data.type === 'telegram_auth_data') {
        handleTelegramAuth(event.data.data);
      }
    };

    checkTelegramDataOnReturn();
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [user]);

  const handleTelegramAuth = async (telegramData) => {
    console.log('🔐 Handling Telegram authentication in App');
    try {
      const result = await loginWithTelegram(telegramData);
      if (result.success) {
        console.log('✅ Telegram auth successful in App');
        setShowTelegramAuth(false);
      } else {
        console.error('❌ Telegram auth failed in App:', result.error);
      }
    } catch (err) {
      console.error('❌ Telegram auth error from App:', err);
    }
  };

  if (apiKeyValid === false) {
    return (
      <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <Card className="auth-card border-danger" style={{ maxWidth: '500px' }}>
          <Card.Body className="p-4 text-center">
            <div className="mb-3">
              <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
            </div>
            <h2 className="text-danger mb-3">🚫 Configuration Error</h2>
            <p className="text-muted mb-4">
              Invalid or missing API Key. Please check your environment configuration.
            </p>
            <div className="bg-light p-3 rounded mb-4 text-start">
              <p className="mb-1"><strong>Current Domain:</strong> {window.location.hostname}</p>
              <p className="mb-1"><strong>API Base URL:</strong> https://taskit.hsbk.ru/api/v1</p>
              <p className="mb-0"><strong>API Key Configured:</strong> {window.env?.VITE_API_KEY ? 'Yes' : 'No'}</p>
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Retry
            </button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (showTelegramAuth) {
    return (
      <div className="d-flex flex-column min-vh-100">
        <Container fluid className="flex-grow-1 p-0">
          <Row className="g-0 h-100">
            <Col lg={6} className="gradient-bg text-white d-none d-lg-flex align-items-center">
              <div className="p-5">
                <div className="text-white-50 mb-5">Admin.Panel</div>
                <h1 className="display-4 fw-bold mb-4">Добро пожаловать в ТаскИТ!</h1>
                <p className="fs-5 mb-5 opacity-75">Ваше решение для управления задачами</p>
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <span className="feature-icon">✓</span>
                    <span className="fs-6">Эффективное управление задачами</span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <span className="feature-icon">✓</span>
                    <span className="fs-6">Совместная работа в команде</span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <span className="feature-icon">✓</span>
                    <span className="fs-6">Организованность и продуктивность</span>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={6} className="d-flex align-items-center justify-content-center bg-light">
              <div className="w-100" style={{ maxWidth: '400px' }}>
                <button 
                  onClick={() => setShowTelegramAuth(false)}
                  className="btn btn-link text-decoration-none p-0 mb-4"
                >
                  <i className="bi bi-arrow-left"></i> Назад к авторизации по Email
                </button>
                
                <Card className="auth-card">
                  <Card.Body className="p-4">
                    <div className="text-center mb-4">
                      <h2 className="h3 mb-2">Вход через Telegram</h2>
                      <p className="text-muted">
                        Безопасная и мгновенная авторизация через Telegram
                      </p>
                    </div>
                    <TelegramAuth 
                      onSuccess={() => {
                        setShowTelegramAuth(false);
                      }} 
                    />
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </Container>

        <footer className="bg-white border-top py-3 mt-auto">
          <Container>
            <div className="text-center">
              <p className="text-muted small mb-0">
                HAKSTER & WASKED | {currentYear} TaskIT<small>.version</small> {APP_VERSION}
              </p>
            </div>
          </Container>
        </footer>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      <Container fluid className="flex-grow-1 p-0">
        <Row className="g-0 h-100">
          <Col lg={6} className="gradient-bg text-white d-none d-lg-flex align-items-center">
            <div className="p-5">
              <div className="text-white-50 mb-5">Admin.Panel</div>
              <h1 className="display-4 fw-bold mb-4">Добро пожаловать в ТаскИТ!</h1>
              <p className="fs-5 mb-5 opacity-75">Ваше решение для управления задачами</p>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center gap-3">
                  <span className="feature-icon">✓</span>
                  <span className="fs-6">Эффективное управление задачами</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className="feature-icon">✓</span>
                  <span className="fs-6">Совместная работа в команде</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className="feature-icon">✓</span>
                  <span className="fs-6">Организованность и продуктивность</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6} className="d-flex align-items-center justify-content-center bg-light">
            <div className="w-100" style={{ maxWidth: '400px' }}>
              <Card className="auth-card">
                <Card.Body className="p-4">
                  <div className="nav nav-tabs-custom nav-fill mb-4">
                    <button 
                      className={`nav-link ${isLoginMode ? 'active' : ''}`}
                      onClick={() => setIsLoginMode(true)}
                    >
                      Войти
                    </button>
                    <button 
                      className={`nav-link ${!isLoginMode ? 'active' : ''}`}
                      onClick={() => setIsLoginMode(false)}
                    >
                      Регистрация
                    </button>
                  </div>

                  {isLoginMode ? (
                    <LoginForm onToggleMode={() => setIsLoginMode(false)} />
                  ) : (
                    <RegisterForm onToggleMode={() => setIsLoginMode(true)} />
                  )}

                  <div className="mt-4">
                    <div className="position-relative text-center mb-3">
                      <hr />
                      <span className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted small">
                        или войдите через
                      </span>
                    </div>
                    <TelegramAuth 
                      onSuccess={() => {
                        console.log('Telegram auth successful');
                      }} 
                    />
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      <footer className="bg-white border-top py-3 mt-auto">
        <Container>
          <div className="text-center">
            <p className="text-muted small mb-0">
              HAKSTER & WASKED | {currentYear} TaskIT<small>.version {APP_VERSION}</small> 
            </p>
          </div>
        </Container>
      </footer>
    </div>
  );
};

// function App() {
//   return (
//     <AuthProvider>
//       <WebSocketProvider>
//         <NotificationProvider>
//           <TasksProvider>
//             <ChatProvider>
//               <Router>
//                 <Routes>
//                   <Route path="/_admin/login" element={
//                     <PublicRoute>
//                       <AuthPage />
//                     </PublicRoute>
//                   } />
//                   <Route path="/_admin" element={
//                     <ProtectedRoute>
//                       <Dashboard />
//                     </ProtectedRoute>
//                   }>
//                     <Route index element={<Navigate to="/_admin/profile" replace />} />
//                     <Route path="profile" element={<ProfileTab />} />
//                     <Route path="tasks" element={<TasksTab />} />
//                     <Route path="users" element={<UsersTab />} />
//                     <Route path="notifications" element={<NotificationsTab />} />
//                     <Route path="settings" element={<SettingsTab />} />
//                     <Route path="chat" element={<ChatPage />} />
//                   </Route>
//                   <Route path="/" element={<Navigate to="/_admin/profile" replace />} />
//                   <Route path="/login" element={<Navigate to="/_admin/login" replace />} />
//                   <Route path="*" element={<Navigate to="/_admin/profile" replace />} />
//                 </Routes>
//               </Router>
//               <ChatToast />
//               </ChatProvider>
//             <NotificationToasts />
//           </TasksProvider>
//         </NotificationProvider>
//       </WebSocketProvider>
//     </AuthProvider>
//   );
// }
function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <NotificationProvider>
          <TasksProvider>
            <ChatProvider>
              <Router>
                <Routes>
                  <Route path="/_admin/login" element={
                    <PublicRoute>
                      <AuthPage />
                    </PublicRoute>
                  } />
                  <Route path="/_admin/inactive" element={<InactiveAccountPage />} />
                  <Route path="/_admin" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/_admin/profile" replace />} />
                    <Route path="profile" element={<ProfileTab />} />
                    <Route path="tasks" element={<TasksTab />} />
                    <Route path="users" element={<UsersTab />} />
                    <Route path="notifications" element={<NotificationsTab />} />
                    <Route path="settings" element={<SettingsTab />} />
                    <Route path="chat" element={<ChatPage />} />
                  </Route>
                  <Route path="/" element={<Navigate to="/_admin/profile" replace />} />
                  <Route path="/login" element={<Navigate to="/_admin/login" replace />} />
                  <Route path="*" element={<Navigate to="/_admin/profile" replace />} />
                </Routes>
          
                <ChatToast />
              </Router>
              <NotificationToasts />
            </ChatProvider>
          </TasksProvider>
        </NotificationProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;